import { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Chip, Stack } from '@mui/material';

//Icon
import CloudUploadIcon from '../../../../assets/images/upload.svg';
import CustomButton from '../Button/CustomButton';
import CustomTypography from '../Typography/CustomTypography';
import CustomTooltip from '../ToolTip/CustomToolTip';

interface CustomDragZoneProps {
  getFiles: (files: File[]) => void;
  uploadBtnRequired?: boolean;
  onClose?: () => void;
  isUploading?: boolean;
}
const CustomDragZone = ({
  getFiles,
  uploadBtnRequired,
  onClose,
  isUploading,
}: CustomDragZoneProps) => {
  const [files, setFiles] = useState<File[]>([]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const removeFile = (indexToRemove: number) => {
    setFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  useEffect(() => {
    if (!uploadBtnRequired) {
      getFiles(files);
    }
  }, [uploadBtnRequired, getFiles]);

  return (
    <Box>
      <Box className="text-left">
        <CustomTypography
          id="upload-documents-title"
          variant="subtitle1"
          className="pb-16"
        >
          Upload Documents
        </CustomTypography>
      </Box>
      {/* Drag & Drop Area */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 4,
          cursor: 'pointer',
          bgcolor: '#f9f9f9',
          textAlign: 'center',
          width: '360px',
          height: '140px',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <input {...getInputProps()} />

        <CustomTypography
          id="drag-and-drop-text"
          variant="body2"
          color="textSecondary"
        >
          Drag & Drop files to upload
        </CustomTypography>
        <Box className="pt-16">
          <CustomButton
            variant="contained"
            startIcon={<CloudUploadIcon />}
            id="file-upload-button"
            color="success"
          >
            Upload
          </CustomButton>
        </Box>
      </Box>

      {/* Upload Button */}

      {/* Display Selected Files */}

      <Stack direction="row" mt={2} flexWrap="wrap">
        {files.map((file, index) => (
          <Box className="mr-12 mb-12" key={`${file.name}-${index}`}>
            <CustomTooltip id="remove-item" title={file.name} placement="top">
              <Chip
                label={file.name}
                onDelete={() => removeFile(index)}
                sx={{
                  bgcolor: '#ffe0b2',
                  color: '#8d4f00',
                  maxWidth: 180,
                }}
              />
            </CustomTooltip>
          </Box>
        ))}
      </Stack>

      {/* Upload Button */}
      {uploadBtnRequired && (
        <Box
          className="flex text-align-end pt-16"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <Box className="mr-16">
            <CustomButton
              id="discard-button"
              color="error"
              variant="outlined"
              onClick={() => {
                onClose?.();
              }}
            >
              Cancel
            </CustomButton>
          </Box>
          <Box>
            <CustomButton
              id="continue-editing-button"
              color="primary"
              variant="contained"
              disabled={files.length === 0 || isUploading}
              onClick={() => {
                if (isUploading) return;
                getFiles(files);
              }}
            >
              {isUploading ? 'Submitting...' : 'Submit'}
            </CustomButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomDragZone;
